import { useEffect, useRef, useState } from "react";
import { pages } from "@microsoft/teams-js";
import {
  Body1,
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Spinner,
  Subtitle1,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { appSchema } from "@app/schema";
import { ListRepository, getSiteByUrl, getTeamSite, type SiteInfo } from "@app/sp-data";
import { graph } from "../data/DataProvider";
import { config } from "../config";
import { useHost } from "../teams/TeamsProvider";
import { describeError } from "../components/ErrorState";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalL, padding: tokens.spacingHorizontalXXL, maxWidth: "560px" },
});

const TAB_NAME = "Gare";
type Source = "team" | "url";

/**
 * Configurazione della scheda di canale.
 * - Con VITE_SITE_URL (sito fisso) verifica solo che le liste esistano: la scheda
 *   funziona in qualsiasi canale, anche privato o condiviso.
 * - Senza, fa scegliere il sito (quello del team o un URL) e lo salva nella scheda.
 */
export function ConfigPage() {
  const styles = useStyles();
  const { groupId, inTeams } = useHost();
  const fixed = !!config.siteUrl;
  const [source, setSource] = useState<Source>(groupId ? "team" : "url");
  const [siteUrl, setSiteUrl] = useState("");
  const [checking, setChecking] = useState(false);
  const [site, setSite] = useState<SiteInfo>();
  const [problem, setProblem] = useState<string>();
  const siteRef = useRef<SiteInfo>();

  async function verify() {
    setChecking(true);
    setProblem(undefined);
    setSite(undefined);
    siteRef.current = undefined;
    if (inTeams) pages.config.setValidityState(false);
    try {
      const s = fixed
        ? await getSiteByUrl(graph, config.siteUrl!)
        : source === "team" && groupId
          ? await getTeamSite(graph, groupId)
          : await getSiteByUrl(graph, siteUrl);
      const missing: string[] = [];
      for (const def of appSchema) {
        try {
          await new ListRepository(graph, s.id, def).id();
        } catch {
          missing.push(def.displayName);
        }
      }
      if (missing.length) {
        setProblem(`Sul sito "${s.displayName}" mancano: ${missing.join(", ")}. Va eseguito il provisioning.`);
        return;
      }
      siteRef.current = s;
      setSite(s);
      if (inTeams) pages.config.setValidityState(true);
    } catch (e) {
      const d = describeError(e);
      setProblem(`${d.title}: ${d.detail}`);
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    if (!inTeams) return;
    pages.config.registerOnSaveHandler((evt) => {
      const s = siteRef.current;
      if (!s) return evt.notifyFailure("Sito non verificato");
      const origin = window.location.origin;
      pages.config
        .setConfig({
          entityId: `gare-${crypto.randomUUID()}`,
          contentUrl: fixed ? `${origin}/tab` : `${origin}/tab?siteId=${encodeURIComponent(s.id)}`,
          websiteUrl: s.webUrl,
          suggestedDisplayName: TAB_NAME,
        })
        .then(() => evt.notifySuccess())
        .catch((e: unknown) => evt.notifyFailure(String(e)));
    });
    if (fixed || groupId) void verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inTeams]);

  return (
    <div className={styles.root}>
      <Subtitle1>Aggiungi la scheda {TAB_NAME}</Subtitle1>
      {fixed ? (
        <Body1>Qui si vedranno le gare e si creeranno le cartelle di gara. I dati restano nel sito SharePoint dell'app.</Body1>
      ) : (
        <>
          <Body1>Scegli il sito SharePoint che contiene gli elenchi dell'app.</Body1>
          <RadioGroup value={source} onChange={(_, d) => setSource(d.value as Source)}>
            <Radio value="team" label="Sito del team corrente" disabled={!groupId} />
            <Radio value="url" label="Un altro sito" />
          </RadioGroup>
          {source === "url" && (
            <Field label="URL del sito">
              <Input value={siteUrl} onChange={(_, d) => setSiteUrl(d.value)} placeholder="https://contoso.sharepoint.com/sites/Progetti" />
            </Field>
          )}
          <div>
            <Button appearance="primary" onClick={verify} disabled={checking || (source === "url" && !siteUrl)}>
              Verifica
            </Button>
          </div>
        </>
      )}

      {checking && <Spinner size="small" label="Verifica in corso…" labelPosition="after" />}
      {site && (
        <MessageBar intent="success">
          <MessageBarBody>Tutto pronto. Premi Salva per aggiungere la scheda.</MessageBarBody>
        </MessageBar>
      )}
      {problem && (
        <MessageBar intent="warning">
          <MessageBarBody>{problem}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
